const DateFormat = require('@/lib/Dates/DateFormat');
const moment = require('moment');
const { isSameDay, toYM, toYMD } = require('@/functions/date');
const { populate } = require('@/functions/objects');
const { toDate, toInt, toString } = require('@/functions/transform');
const { toNullableString } = require('@/functions/transform');

/**
 * @param {object} [data]
 *
 * @constructor
 */
const Session = function (data = {}) {
    /**
     * @type {?number}
     */
    this.ID = null;

    /**
     * @type {?number}
     */
    this.branchID = null;

    /**
     * If the name is null it means the Session's name is exactly the same as the Event's name.
     *
     * @type {?string}
     */
    this.name = null;

    /**
     * This is a Date constructed from the date and time returned from the API. That means it is always (e.g)
     * 2023-05-23 14:45 local time wherever this is being looked at.
     *
     * Conversely, this.startsAt is constructed from a string that contains the timezone, so it will be the actual
     * time the session starts.
     *
     * @type {Date}
     */
    this.startTime = null;

    /**
     * @type {Number}
     */
    this.startTimeYear = null;

    /**
     * @type {Number}
     */
    this.startTimeMonth = null;

    /**
     * @type {Number}
     */
    this.startTimeDay = null;

    /**
     * Start time of the session in the format "YYYY-MM-DD".
     *
     * @type {String}
     */
    this.startTimeYMD = null;

    /**
     * Start time of the session in the format "YYYY-MM".
     *
     * @type {String}
     */
    this.startTimeYM = null;

    /**
     * @type {Date}
     */
    this.endTime = null;

    /**
     * @type {boolean}
     */
    this.multiDay = false;

    /**
     * @type {?number}
     */
    this.eventID = null;

    /**
     * Total capacity of the event.
     *
     * @type {number}
     */
    this.capacity = 0;

    /**
     * Number of spaces already sold in orders.
     *
     * @type {number}
     */
    this.spacesSold = 0;

    /**
     * Number of spaces currently held in reservations.
     *
     * @type {number}
     */
    this.spacesReserved = 0;

    /**
     * Remaining capacity of the event.
     * Note there is no real time update of this value. It is the value at the time the sessions were synced only!
     *
     * @type {number}
     */
    this.spacesAvailable = 0;

    /**
     * Custom price for this session.
     *
     * @type {?number}
     */
    this.sessionPrice = null;

    /**
     * @type {?string}
     */
    this.status = null;

    /**
     * This is another Date representation of the start time of the session, however this one is built from
     * an ISO string returned from the API with the appropriate timezone.
     * That means this Date is the correct UTC start time of the session, and not just the date/time interpreted
     * as local time.
     *
     * @type {Date}
     */
    this.startsAt = null;

    /**
     * This is another Date representation of the end time of the session, however this one is built from
     * an ISO string returned from the API with the appropriate timezone.
     * That means this Date is the correct UTC start time of the session, and not just the date/time interpreted
     * as local time.
     *
     * @type {Date}
     */
    this.endsAt = null;

    populate(this, data);
};

Session.prototype = {
    getHydrationMap() {
        return {
            branchID: toInt,
            capacity: toInt,
            endsAt: toDate,
            // Beware! Most of the time when you load a session from the API, the ID of the event will be called eventID
            // But if you load an order and its attached lines, the session attached to the line's eventID will be the
            // sessionID and you actually need to use eventCatID!
            eventID: {
                field: ['eventCatID', 'eventID'],
                transform: toInt,
            },
            ID: {
                field: ['ID', 'eventID'],
                transform: toInt,
            },
            name: toNullableString,
            // The original sync will still include spacesAvailable because we need to know something for
            // offline sales to work.
            spacesAvailable: toInt,
            status: toString,
            startsAt: toDate,
            startTime: {
                field: '*',
                transform(value) {
                    // After hydration the startDate and startTime properties are combined to a single Date object.
                    if (value.startTime) {
                        return toDate(value.startTime);
                    }

                    // Before hydration there will be separate date and time properties.
                    if (value.date && value.time) {
                        return toDate(value.date + ' ' + value.time);
                    }

                    return null;
                },
            },
            endTime: {
                field: '*',
                transform(value) {
                    // We need to do a special check to see if the data is already hydrated, because
                    // 'endTime' exists in both the API data and model data but means different things.
                    // From the API there may be endDate (Y-m-d) and/or endTime (H:i:s)
                    // In the model there is endTime (Date object)
                    if (value.endTime && value.isHydrated) {
                        return toDate(value.endTime);
                    }

                    // Webkit (Safari and browsers on iOS) expect datetime strings in strict ISO 8601 format otherwise
                    // new Date() returns 'Invalid date' although other browsers are fine. Adding 'T' fixes the issue.
                    // e.g. 2022-11-15 09:10:11 -> 2022-11-15T09:10:11
                    if (value.endDate != null && value.endTime != null) {
                        return new Date(value.endDate + 'T' + value.endTime);
                    } else if (value.endTime != null) {
                        return new Date(value.date + 'T' + value.endTime);
                    }

                    return null;
                },
            },
        };
    },

    /**
     * @param {object} data
     */
    afterHydration(data = {}) {
        if (this.startTime) {
            this.startTimeYear = this.startTime.getFullYear();
            this.startTimeMonth = this.startTime.getMonth();
            this.startTimeDay = this.startTime.getDate();
            this.startTimeYMD = toYMD(this.startTime);
            this.startTimeYM = toYM(this.startTime);
        }

        if (this.startTime && this.endTime) {
            this.multiDay = !isSameDay(this.startTime, this.endTime);
        }

        this.setSpacesFromData(data);
    },

    getFullName() {
        let n = '';

        if (this.name) {
            n += this.name + ' - ';
        }

        const momentStart = moment(this.startTime);
        const startDateString = momentStart.format(DateFormat.DATE);
        const startTimeString = momentStart.format(DateFormat.TIME);

        const momentEnd = this.endTime ? moment(this.endTime) : null;
        const endDateString = momentEnd ? momentEnd.format(DateFormat.DATE) : null;
        const endTimeString = momentEnd ? momentEnd.format(DateFormat.TIME) : null;

        n += startDateString;

        if (this.multiDay) {
            n += ' @ ' + startTimeString + ' - ' + endDateString + ' @ ' + endTimeString;
        } else if (this.endTime) {
            n += ' (' + startTimeString + ' - ' + endTimeString + ')';
        } else {
            n += ' @ ' + startTimeString;
        }

        return n;
    },

    /**
     * Get a Date object for midnight on the start date for this session.
     *
     * @return {?Date}
     */
    getStartDate() {
        if (this.startTime instanceof Date) {
            let n = new Date(this.startTime);
            n.setHours(0);
            n.setMinutes(0);
            n.setSeconds(0);
            n.setMilliseconds(0);
            return n;
        }

        return null;
    },

    /**
     * @param {{spacesAvailable: number, spacesSold:number, spacesReserved: number}} data
     */
    setSpacesFromData(data) {
        if (data.hasOwnProperty('spacesSold')) {
            this.spacesSold = (data.spacesSold || 0);
        }
        if (data.hasOwnProperty('spacesReserved')) {
            this.spacesReserved = (data.spacesReserved || 0);
        }

        if (data.hasOwnProperty('spacesAvailable')) {
            this.spacesAvailable = data.spacesAvailable;
        }
    },

    /**
     * Update the spacesAvailable, spacesSold, and spacesReserved properties from a SessionAvailability object.
     *
     * @param {SessionAvailability} sessionAvailability
     */
    setSpacesFromAvailability(sessionAvailability) {
        if (sessionAvailability.spacesAvailable !== null) {
            this.spacesAvailable = sessionAvailability.spacesAvailable;
        }

        if (sessionAvailability.spacesSold !== null) {
            this.spacesSold = sessionAvailability.spacesSold;
        }

        if (sessionAvailability.spacesReserved !== null) {
            this.spacesReserved = sessionAvailability.spacesReserved;
        }
    },

    /**
     * Handle loading sessions from IndexedDB that were stored before PRO-1705 and have an available property
     * instead of a spacesAvailable property.
     *
     * @param value
     */
    set available(value) {
        this.spacesAvailable = value;
    },

    /**
     * Check if the start date/time interpreted as local time is different to the absolute startsAt time provided
     * by the API. If it is, this session is in a different timezone to that of the current browser.
     *
     * @return {null|boolean}
     */
    isDifferentTimezone() {
        if (!this.startsAt || !this.startTime) {
            // This may happen with sessions loaded from IndexedDB shortly after PROP-835 is released.
            // Once everybody has done a sync since then, startsAt should always be populated.
            // startTime should already always be populated, but it doesn't hurt to check.
            return null;
        }

        return this.startsAt.getTime() !== this.startTime.getTime();
    },
};

module.exports = Session;
